/**
 * The owner's frame load for a project (Part 04 decision 2).
 *
 * - Canvas: the full list replaces every frame, as it always did (Denker's
 *   off-wall frames from other projects included).
 * - Wall: a project switch clears the frames store first, all but Denker's
 *   off-wall frames (`owner-extra-projects.ts`). The off-wall frames the chat
 *   and question code reads arrive in full and merge; the Pinned
 *   row and the first wall page arrive as summaries.
 *
 * Mirrors receive the frames through the mirror channel and page their own
 * feeds.
 */
import { listFrames } from "@/api/spaces";
import { denkerFrameKeep } from "@/desktop/owner-extra-projects";
import { ingestFrames } from "@/lib/frame-ingest";
import {
  loadNextWallPage,
  loadWallTools,
  resetWallFeed,
} from "@/lib/wall-feed-loader";
import { OFF_WALL_FRAME_TYPES } from "@/lib/wall-membership";
import { useFramesStore } from "@/stores/frames-store";
import { isWallActive } from "@/stores/workspace-view-store";

export interface WorkspaceFrameLoadOptions {
  signal: AbortSignal;
  /** False once the load no longer applies (another load, project or account). */
  shouldApply: () => boolean;
}

/** The project whose frames the wall holds; a different one clears them. */
let wallProjectId: string | null = null;

export async function loadWorkspaceFrames(
  projectId: string,
  { signal, shouldApply }: WorkspaceFrameLoadOptions,
): Promise<void> {
  if (!isWallActive()) {
    wallProjectId = null;
    const frames = await listFrames(projectId, { signal, silent: true });
    if (shouldApply()) ingestFrames(frames);
    return;
  }
  if (wallProjectId !== projectId) {
    useFramesStore.getState().clear(denkerFrameKeep());
    wallProjectId = projectId;
  }
  const offWall = await listFrames(projectId, {
    signal,
    silent: true,
    frameTypes: OFF_WALL_FRAME_TYPES,
  });
  if (!shouldApply()) return;
  ingestFrames(offWall);
  // A reload of the same project starts the feed over from the newest cards.
  resetWallFeed(projectId);
  await Promise.all([
    loadWallTools(projectId, { signal }),
    loadNextWallPage(projectId, { signal }),
  ]);
}
